// UI state for the dashboard monitor that must survive leaving the route (e.g.
// opening a conversation and coming back). Kept in a store — NOT component
// state — so returning to /dashboard restores the same tab, the same chat-
// history filters, and the last-fetched history rows instantly, with only a
// background refresh instead of a blank re-load.

import { create } from 'zustand';
import type { HistoryFilters, HistoryItem, InboxPriorityValue, InboxStatusValue } from '../lib/history-api';

export type MonitorMode = 'monitor' | 'history';
export type InternalFilter = 'all' | 'only' | 'hide';

export interface HistoryQuery {
  q: string;
  agentId: string;
  status: InboxStatusValue | '';
  priority: InboxPriorityValue | '';
  tag: string;
  departmentId: string;
  siteId: string;
  location: string;
  engagedOnly: boolean;
  internal: InternalFilter;
  limit: number;
  from: string;
  to: string;
  page: number;
}

export const DEFAULT_HISTORY_QUERY: HistoryQuery = {
  q: '',
  agentId: '',
  status: '',
  priority: '',
  tag: '',
  departmentId: '',
  siteId: '',
  location: '',
  engagedOnly: false,
  internal: 'all',
  limit: 25,
  from: '',
  to: '',
  page: 1,
};

interface MonitorUiState {
  /** Which dashboard tab is active — restored on return so "back" lands where
   *  the agent came from (live monitor vs chat history). */
  mode: MonitorMode;
  setMode: (m: MonitorMode) => void;

  historyQuery: HistoryQuery;
  patchHistoryQuery: (patch: Partial<HistoryQuery>) => void;
  resetHistoryQuery: () => void;

  /** Last chat-history page fetched. Shown immediately on return while a
   *  background refresh updates it. Null until the first fetch lands. */
  historyResult: { items: HistoryItem[]; total: number } | null;
  setHistoryResult: (r: { items: HistoryItem[]; total: number }) => void;

  /** Filter option lists (sites/agents/countries) for the dropdowns. */
  historyFilters: HistoryFilters;
  setHistoryFilters: (f: HistoryFilters) => void;
}

export const useMonitorUi = create<MonitorUiState>((set) => ({
  mode: 'monitor',
  setMode: (mode) => set({ mode }),

  historyQuery: { ...DEFAULT_HISTORY_QUERY },
  // Changing any filter invalidates the current page: it describes a different
  // result set, so page 5 of the old one is meaningless and usually empty.
  // The reset happens HERE, in the same state update as the filter change,
  // rather than in a follow-up effect in the component. Doing it in a second
  // update meant every filter change fired two fetches — the first for the
  // stale page (a deep OFFSET into a set that no longer exists), the second
  // for page 1. Mounting doesn't patch, so returning to the tab still keeps
  // the page the agent was on.
  patchHistoryQuery: (patch) =>
    set((s) => {
      const changesFilter = Object.keys(patch).some((k) => k !== 'page');
      return {
        historyQuery: {
          ...s.historyQuery,
          ...patch,
          ...(changesFilter && patch.page === undefined ? { page: 1 } : {}),
        },
      };
    }),
  resetHistoryQuery: () => set({ historyQuery: { ...DEFAULT_HISTORY_QUERY } }),

  historyResult: null,
  setHistoryResult: (historyResult) => set({ historyResult }),

  historyFilters: { agents: [], countries: [], sites: [], tags: [], departments: [] },
  setHistoryFilters: (historyFilters) => set({ historyFilters }),
}));
